import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useUserStore } from '@/manage/stores/user'
import { userApi } from '@/manage/api/user'
import type { UserTag } from '@/manage/types/user'
import { logError } from '@/utils/error'

export const useTagStore = defineStore('tag', () => {
  const userStore = useUserStore()
  let generation = 0
  const TAG_SUMMARY_LIMIT = 5
  const tags = ref<UserTag[]>([])
  const totalCandidateCount = ref<number | null>(null)
  const totalTagCount = ref<number | null>(null)
  const defaultTagsFetchedAt = ref<number | null>(null)
  const summaryFetchedAt = ref<number | null>(null)
  const loading = ref(false)
  const summaryLoading = ref(false)
  const hasMore = ref(true)
  const page = ref(1)
  const pageSize = ref(15)
  const query = ref<string | null>(null)
  const refreshTrigger = ref(0)

  const triggerRefresh = () => {
    refreshTrigger.value++
  }

  const fetchTags = async (options?: { reset?: boolean; query?: string | null }) => {
    if (loading.value) return
    loading.value = true
    const requestGeneration = generation
    try {
      if (options?.reset) {
        page.value = 1
        hasMore.value = true
        tags.value = []
      }
      if (options?.query !== undefined) {
        query.value = options.query
      }
      if (!hasMore.value && !options?.reset) {
        return
      }
      const resp = await userApi.getUserTags({
        page: page.value,
        limit: pageSize.value,
        query: query.value || null,
      })
      if (requestGeneration !== generation) return
      const items = (resp?.item || []).map((tag) => ({
        ...tag,
        is_default: Boolean(tag.is_default),
        vip_tag: Boolean(tag.vip_tag),
      }))

      // Backend optimization: first page can return aggregate candidate count.
      if (page.value === 1 && !query.value) {
        defaultTagsFetchedAt.value = Date.now()

        if (typeof resp?.total === 'number') {
          totalTagCount.value = Math.max(0, resp.total)
        } else {
          totalTagCount.value = items.length
        }

        if (typeof resp?.candidate_count === 'number') {
          totalCandidateCount.value = Math.max(0, resp.candidate_count)
        } else {
          totalCandidateCount.value = items.reduce(
            (sum, tag) => sum + Math.max(0, Number(tag.candidate_count) || 0),
            0,
          )
        }
        summaryFetchedAt.value = Date.now()
      }

      if (page.value === 1) {
        tags.value = items
      } else {
        tags.value = [...tags.value, ...items]
      }
      hasMore.value = !!resp?.has_more
      if (hasMore.value) {
        page.value += 1
      }
    } catch (error) {
      logError('tag-store fetchTags failed', error)
    } finally {
      if (requestGeneration === generation) loading.value = false
    }
  }

  const updateTagCandidateCount = (tagId: string, count: number) => {
    const target = tags.value.find((tag) => String(tag.id) === String(tagId))
    if (!target) return
    target.candidate_count = Math.max(0, Number(count) || 0)
  }

  const upsertTagLocal = (tag: UserTag) => {
    const targetId = String(tag.id || '').trim()
    if (!targetId) return

    const normalizedTag: UserTag = {
      ...tag,
      id: targetId,
      name: String(tag.name || '').trim(),
      is_top: Boolean(tag.is_top),
      is_default: Boolean(tag.is_default),
      candidate_count: Math.max(0, Number(tag.candidate_count) || 0),
      vip_tag: Boolean(tag.vip_tag),
    }
    const existingIndex = tags.value.findIndex((item) => String(item.id || '').trim() === targetId)
    const normalizedQuery = String(query.value || '')
      .trim()
      .toLowerCase()
    const matchesQuery =
      !normalizedQuery || normalizedTag.name.toLowerCase().includes(normalizedQuery)

    if (existingIndex >= 0) {
      tags.value = tags.value.map((item, index) => (index === existingIndex ? normalizedTag : item))
    } else if (matchesQuery) {
      tags.value =
        normalizedTag.is_default || normalizedTag.is_top
          ? [normalizedTag, ...tags.value]
          : [...tags.value, normalizedTag]
      if (typeof totalTagCount.value === 'number') {
        totalTagCount.value += 1
      }
      if (typeof totalCandidateCount.value === 'number') {
        totalCandidateCount.value += normalizedTag.candidate_count
      }
    }

    defaultTagsFetchedAt.value = Date.now()
    summaryFetchedAt.value = Date.now()
  }

  const patchTagLocal = (tagId: string, patch: Partial<UserTag>) => {
    const targetId = String(tagId || '').trim()
    if (!targetId) return
    tags.value = tags.value.map((tag) =>
      String(tag.id || '').trim() === targetId
        ? {
            ...tag,
            ...patch,
            is_default:
              patch.is_default !== undefined ? Boolean(patch.is_default) : Boolean(tag.is_default),
            vip_tag: patch.vip_tag !== undefined ? Boolean(patch.vip_tag) : Boolean(tag.vip_tag),
          }
        : tag,
    )
    defaultTagsFetchedAt.value = Date.now()
  }

  const removeTagLocal = (tagId: string) => {
    const targetId = String(tagId || '').trim()
    const removed = tags.value.find((tag) => String(tag.id || '').trim() === targetId)
    tags.value = tags.value.filter((tag) => String(tag.id || '').trim() !== targetId)
    if (removed) {
      if (typeof totalTagCount.value === 'number') {
        totalTagCount.value = Math.max(0, totalTagCount.value - 1)
      }
      if (typeof totalCandidateCount.value === 'number') {
        totalCandidateCount.value = Math.max(
          0,
          totalCandidateCount.value - Math.max(0, Number(removed.candidate_count) || 0),
        )
      }
      defaultTagsFetchedAt.value = Date.now()
      summaryFetchedAt.value = null
    }
  }

  const isFresh = (fetchedAt: number | null, ttlMs = 60_000) =>
    typeof fetchedAt === 'number' && Date.now() - fetchedAt < ttlMs

  const ensureDefaultTagsLoaded = async (ttlMs = 60_000) => {
    if (loading.value) return
    const hasDefaultList = tags.value.length > 0 && query.value == null
    if (hasDefaultList && isFresh(defaultTagsFetchedAt.value, ttlMs)) {
      return
    }
    await fetchTags({ reset: true, query: null })
  }

  const ensureSummaryLoaded = async (ttlMs = 60_000) => {
    if (summaryLoading.value) return
    const hasSummary =
      typeof totalCandidateCount.value === 'number' && typeof totalTagCount.value === 'number'
    if (hasSummary && isFresh(summaryFetchedAt.value, ttlMs)) {
      return
    }
    summaryLoading.value = true
    const requestGeneration = generation
    try {
      const resp = await userApi.getUserTags({ page: 1, limit: TAG_SUMMARY_LIMIT, query: null })
      if (requestGeneration !== generation) return
      if (typeof resp?.total === 'number') {
        totalTagCount.value = Math.max(0, resp.total)
      }
      if (typeof resp?.candidate_count === 'number') {
        totalCandidateCount.value = Math.max(0, resp.candidate_count)
      }
      summaryFetchedAt.value = Date.now()
    } catch (error) {
      logError('tag-store ensureSummaryLoaded failed', error)
    } finally {
      if (requestGeneration === generation) summaryLoading.value = false
    }
  }

  const invalidateDefaultTagsCache = () => {
    defaultTagsFetchedAt.value = null
  }

  const invalidateSummaryCache = () => {
    summaryFetchedAt.value = null
  }

  watch(
    () => [userStore.user?.sub, userStore.isAdminModeActive],
    () => {
      generation++
      tags.value = []
      totalCandidateCount.value = null
      totalTagCount.value = null
      defaultTagsFetchedAt.value = null
      summaryFetchedAt.value = null
      loading.value = false
      summaryLoading.value = false
      hasMore.value = true
      page.value = 1
      query.value = null
    },
    { flush: 'sync' },
  )

  return {
    tags,
    totalCandidateCount,
    totalTagCount,
    loading,
    hasMore,
    page,
    pageSize,
    query,
    refreshTrigger,
    triggerRefresh,
    fetchTags,
    updateTagCandidateCount,
    upsertTagLocal,
    patchTagLocal,
    removeTagLocal,
    ensureDefaultTagsLoaded,
    ensureSummaryLoaded,
    invalidateDefaultTagsCache,
    invalidateSummaryCache,
  }
})
